import { useCallback, useEffect, useRef, useState } from "react";
import type { PlyrEvent } from "plyr";

const REQUIRED_PROGRESS = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

export const useTrackFeaturedMediaCompletion = ({ lesson }: any) => {
  const [storedProgress, setStoredProgress] = useState<null | number>(null);
  const [hasFeaturedMediaEnded, setHasFeaturedMediaEnded] = useState(false);
  const [featuredMediaProgress, setFeaturedMediaProgress] = useState(new Set());
  const [isFeaturedMediaCompleted, setIsFeaturedMediaCompleted] =
    useState(false);
  const storedProgressRef = useRef(storedProgress);

  useEffect(() => {
    storedProgressRef.current = storedProgress;
  }, [storedProgress]);

  useEffect(() => {
    setStoredProgress(null);
    setFeaturedMediaProgress(new Set());
    setIsFeaturedMediaCompleted(false);
    setHasFeaturedMediaEnded(false);
  }, [lesson?.id]);

  const onFeaturedMediaProgress = useCallback((event: PlyrEvent) => {
    const plyrRef = event.detail.plyr;
    const currentPercent = (plyrRef.currentTime / plyrRef.duration) * 100;
    const currentProgressStep = Math.ceil(currentPercent / 10) * 10; // Round to the upper nearest 10

    if (plyrRef.duration > 0 && plyrRef.currentTime === plyrRef.duration) {
      setHasFeaturedMediaEnded(true);
    }

    let storedMediaProgressArray: any;

    // When users watch a video, we save their current progress in the local storage.
    // If they come back, they will start were they left it, so we will mark all previous parts
    // of the featured media as "watched".
    // this is a naive implementation, we trust that if a user has reached a certain progress
    // and it's coming back, it is because they have watched the whole featured media up to there.
    if (!storedProgressRef.current) {
      setStoredProgress(currentProgressStep);
      const length = Math.ceil(currentProgressStep / 10);
      storedMediaProgressArray = Array.from(
        { length },
        (_, index) => (index + 1) * 10,
      );
    }

    setFeaturedMediaProgress(prev => {
      const newSet = new Set(storedMediaProgressArray ?? prev);
      newSet.add(currentProgressStep);

      if (newSet.size === prev.size) {
        return prev; // to prevent unnecessary re-renders
      }

      return newSet;
    });
  }, []);

  if (
    !isFeaturedMediaCompleted &&
    REQUIRED_PROGRESS.every(number => featuredMediaProgress.has(number))
  ) {
    setIsFeaturedMediaCompleted(true);
  }

  return {
    onFeaturedMediaProgress,
    isFeaturedMediaCompleted,
    hasFeaturedMediaEnded,
  };
};
